import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import courseService from '../services/courseService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CourseEditor.css';

const CourseEditor = () => {
  const { appId, courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  // Check if we're on the new course page by examining the URL path
  const isNewCoursePage = location.pathname.endsWith('/courses/new');
  const isNewCourse = isNewCoursePage || courseId === 'new';
  
  console.log('Current path:', location.pathname);
  console.log('Is new course page:', isNewCoursePage);
  console.log('Course ID from params:', courseId);
  console.log('Is new course:', isNewCourse);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    is_published: false
  });
  
  const [loading, setLoading] = useState(!isNewCourse);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  
  // Check if user has app_editor role
  const isAppEditor = currentUser?.roles?.some(role => 
    role.name === 'app_editor' || role.name === 'admin' || role.name === 'superadmin'
  );
  
  useEffect(() => {
    if (!isNewCourse) {
      fetchCourse();
    } else {
      // For new courses, reset the form to default values and stop loading
      setFormData({
        title: '',
        description: '',
        thumbnail_url: '',
        is_published: false
      });
      setLoading(false);
      console.log('Initializing new course form');
    }
  }, [courseId, isNewCourse]);
  
  const fetchCourse = async () => {
    try {
      setLoading(true);
      console.log(`🔧 CourseEditor: Fetching course ${courseId} for app ${appId}`);
      
      // Get the auth token
      const token = localStorage.getItem('token_admin');
      // Get the current domain
      const domain = window.location.hostname;
      
      console.log("🔧 CourseEditor: Auth token present:", !!token);
      
      // Use direct fetch with the app-specific endpoint that we know works
      const numericAppId = parseInt(appId, 10) || 1;
      const response = await fetch(`/api/apps/${numericAppId}/courses/${courseId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-App-Domain': domain,
          'X-SaltMarsh-App-Context': 'admin'
        },
        credentials: 'include'
      });
      
      console.log("🔧 CourseEditor: Response status:", response.status);
      
      if (response.status === 200) {
        const data = await response.json();
        console.log("🔧 CourseEditor: Course data received:", data);
        
        // Extract course data from possible response structures
        const courseData = data.data || data.course || data;
        
        setFormData({
          title: courseData.title || '',
          description: courseData.description || '',
          thumbnail_url: courseData.thumbnail_url || '',
          is_published: courseData.is_published || false
        });
        
        if (courseData.thumbnail_url) {
          setThumbnailPreview(courseData.thumbnail_url);
        }
        
        setError(null);
      } else {
        console.error("🔧 CourseEditor: Failed to fetch course, status:", response.status);
        setError(`Failed to load course. Server returned status ${response.status}.`);
      }
    } catch (err) {
      console.error('🔧 CourseEditor: Error fetching course:', err);
      setError('Failed to load course. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Handle thumbnail upload if there's a new file
      let thumbnailUrl = formData.thumbnail_url;
      if (thumbnailFile) {
        // In a real app, you would upload the thumbnail to a server
        // and get back a URL. For now, we'll just use the preview URL.
        thumbnailUrl = thumbnailPreview;
      }
      
      const courseData = {
        ...formData,
        thumbnail_url: thumbnailUrl
      };
      
      console.log('🔧 CourseEditor: Submitting course data:', courseData);
      console.log('🔧 CourseEditor: App ID:', appId);
      console.log('🔧 CourseEditor: Course ID:', courseId);
      console.log('🔧 CourseEditor: Is New Course:', isNewCourse);
      console.log('🔧 CourseEditor: Current URL path:', location.pathname);
      
      // Get the auth token
      const token = localStorage.getItem('token_admin');
      // Get the current domain
      const domain = window.location.hostname;
      
      let savedCourse;
      try {
        // Make sure appId is a valid number
        const numericAppId = parseInt(appId, 10);
        if (isNaN(numericAppId)) {
          throw new Error(`Invalid app ID: ${appId}`);
        }
        
        // Set up common request options
        const requestOptions = {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
            'X-App-Domain': domain,
            'X-SaltMarsh-App-Context': 'admin'
          },
          credentials: 'include'
        };
        
        // Force create mode if we're on the /courses/new path
        if (isNewCourse || isNewCoursePage || location.pathname.endsWith('/courses/new')) {
          console.log('🔧 CourseEditor: Creating new course with endpoint:', `/api/apps/${numericAppId}/courses`);
          
          const response = await fetch(`/api/apps/${numericAppId}/courses`, {
            method: 'POST',
            ...requestOptions,
            body: JSON.stringify(courseData)
          });
          
          if (response.status === 201 || response.status === 200) {
            savedCourse = await response.json();
            console.log('🔧 CourseEditor: Course created successfully:', savedCourse);
          } else {
            const errorData = await response.json();
            throw new Error(`Failed to create course: ${errorData.message || response.statusText}`);
          }
        } else {
          // Make sure courseId is a valid number
          if (!courseId) {
            throw new Error('Course ID is missing');
          }
          
          const numericCourseId = parseInt(courseId, 10);
          if (isNaN(numericCourseId)) {
            throw new Error(`Invalid course ID: ${courseId}`);
          }
          
          console.log('🔧 CourseEditor: Updating course with endpoint:', `/api/apps/${numericAppId}/courses/${numericCourseId}`);
          
          const response = await fetch(`/api/apps/${numericAppId}/courses/${numericCourseId}`, {
            method: 'PUT',
            ...requestOptions,
            body: JSON.stringify(courseData)
          });
          
          if (response.status === 200) {
            savedCourse = await response.json();
            console.log('🔧 CourseEditor: Course updated successfully:', savedCourse);
          } else {
            const errorData = await response.json();
            throw new Error(`Failed to update course: ${errorData.message || response.statusText}`);
          }
        }
        
        // Navigate to the course management page
        navigate(`/apps/${appId}/courses`);
      } catch (apiError) {
        console.error('🔧 CourseEditor: API Error details:', {
          message: apiError.message,
          response: apiError.response ? {
            status: apiError.response.status,
            statusText: apiError.response.statusText,
            data: apiError.response.data
          } : 'No response data',
          request: apiError.request ? 'Request object exists' : 'No request object'
        });
        
        // Set a more detailed error message
        if (apiError.response && apiError.response.data && apiError.response.data.message) {
          setError(`Server error: ${apiError.response.data.message}`);
        } else if (apiError.response && apiError.response.status === 500) {
          setError('Server error (500): The server encountered an unexpected condition. Please check if all required fields are filled correctly.');
        } else {
          setError(`Failed to save course: ${apiError.message}`);
        }
      }
    } catch (err) {
      console.error('🔧 CourseEditor: Error in form submission:', err);
      setError('Failed to process form. Please try again later.');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isAppEditor) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          You do not have permission to access this page. Only app editors can manage courses.
        </div>
      </div>
    );
  }
  
  return (
    <div className="course-editor container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{isNewCourse ? 'Create New Course' : 'Edit Course'}</h1>
      </div>
      
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="title" className="form-label">Course Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="description" className="form-label">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  rows="5"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </div>
              
              <div className="mb-3">
                <label htmlFor="thumbnail" className="form-label">Thumbnail Image</label>
                <input
                  type="file"
                  id="thumbnail"
                  name="thumbnail"
                  className="form-control"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                />
                <small className="form-text text-muted">
                  Recommended size: 1280x720 pixels (16:9 ratio)
                </small>
                
                {thumbnailPreview && (
                  <div className="mt-2 thumbnail-preview-container">
                    <img 
                      src={thumbnailPreview} 
                      alt="Thumbnail preview" 
                      className="thumbnail-preview"
                    />
                  </div>
                )}
              </div>
              
              <div className="mb-4 form-check">
                <input
                  type="checkbox"
                  id="is_published"
                  name="is_published"
                  className="form-check-input"
                  checked={formData.is_published}
                  onChange={handleInputChange}
                />
                <label htmlFor="is_published" className="form-check-label">
                  Publish this course (make it visible to users)
                </label>
              </div>
              
              <div className="d-flex gap-2">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    isNewCourse ? 'Create Course' : 'Save Changes'
                  )}
                </button>
                
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => navigate(`/apps/${appId}/courses`)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseEditor; 